/**
 * persistentSettings.js
 *
 * Durable key/value storage for user-facing settings that MUST survive app
 * restarts, crashes, and OS-driven process kills (e.g. language, printer
 * selection, restaurant, UI preferences).
 *
 * Strategy: double-write to two independent backends and read-through with
 * automatic healing (if one backend is missing the value, it is re-seeded
 * from the other).
 *
 *   1. SecureStore (expo-secure-store) — backed by Android EncryptedSharedPreferences
 *      / iOS Keychain. Survives app updates and is NOT cleared by redux-persist
 *      purges. Values are capped at 2 KB each, so only small scalars go here.
 *   2. AsyncStorage — backed by redux-persist-filesystem-storage on Android
 *      (unlimited size). Primary store for everything.
 *
 * Any setting persisted through this helper is essentially permanent from the
 * user's point of view.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

let _secureStoreModule = null;
const getSecureStore = () => {
    if (_secureStoreModule !== null) return _secureStoreModule;
    try {
        _secureStoreModule = require('expo-secure-store');
    } catch (_) {
        _secureStoreModule = null;
    }
    return _secureStoreModule;
};

// SecureStore only allows [A-Za-z0-9._-], so we sanitize keys for it.
const secureKey = (key) => `ps_${String(key).replace(/[^A-Za-z0-9._-]/g, '_')}`;

/**
 * Persist a setting to BOTH AsyncStorage and SecureStore.
 * Writes are awaited but failures in one backend do not abort the other.
 */
export const setSetting = async (key, value) => {
    const serialized = value === null || value === undefined
        ? null
        : (typeof value === 'string' ? value : JSON.stringify(value));

    const tasks = [];

    if (serialized === null) {
        tasks.push(AsyncStorage.removeItem(key).catch((e) =>
            console.warn(`[persistentSettings] AsyncStorage.removeItem(${key}) failed`, e?.message)
        ));
        const secure = getSecureStore();
        if (secure?.deleteItemAsync) {
            tasks.push(secure.deleteItemAsync(secureKey(key)).catch(() => {}));
        }
    } else {
        tasks.push(AsyncStorage.setItem(key, serialized).catch((e) =>
            console.warn(`[persistentSettings] AsyncStorage.setItem(${key}) failed`, e?.message)
        ));
        const secure = getSecureStore();
        // SecureStore has a ~2 KB value limit; only use it for small scalars.
        if (secure?.setItemAsync && serialized.length < 1800) {
            tasks.push(secure.setItemAsync(secureKey(key), serialized).catch(() => {}));
        }
    }

    await Promise.all(tasks);
};

/**
 * Read a setting. Falls back to SecureStore if AsyncStorage is missing the
 * value (e.g. after a redux-persist corruption or fresh install with backup
 * restore). When the fallback succeeds, the value is re-seeded into
 * AsyncStorage so the next read is fast.
 */
export const getSetting = async (key, defaultValue = null) => {
    try {
        const primary = await AsyncStorage.getItem(key);
        if (primary !== null && primary !== undefined) return primary;
    } catch (e) {
        console.warn(`[persistentSettings] AsyncStorage.getItem(${key}) failed`, e?.message);
    }

    const secure = getSecureStore();
    if (secure?.getItemAsync) {
        try {
            const backup = await secure.getItemAsync(secureKey(key));
            if (backup !== null && backup !== undefined) {
                // Re-seed primary store so future reads are fast.
                AsyncStorage.setItem(key, backup).catch(() => {});
                return backup;
            }
        } catch (_) {
            // ignore — SecureStore unavailable
        }
    }

    return defaultValue;
};

export const getSettingJSON = async (key, defaultValue = null) => {
    const raw = await getSetting(key, null);
    if (raw === null || raw === undefined) return defaultValue;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return defaultValue;
    }
};

export const removeSetting = (key) => setSetting(key, null);

export default { setSetting, getSetting, getSettingJSON, removeSetting };

